/**
 * Equipment WebSocket wire-shape constants for the dispatcher-UI
 * fleet status surface. Backend gateway
 * (`backend-tms/src/equipment/gateways/equipment.gateway.ts`) emits
 * these events to the per-tenant `tenant:{id}:equipment` room and the
 * frontend hook (`apps/web-tms/src/hooks/use-vehicle-status.ts`)
 * subscribes to them — both sides import from this file so a rename
 * touches one place.
 *
 * Issue #422 step 3.
 */

/**
 * Room-scope tag passed to `TenantRoomManager` when stamping the
 * tenant room name (`tenant:{id}:equipment`). Mirrors
 * `FINANCE_WS_ROOM_SCOPE` precedent.
 */
export const EQUIPMENT_WS_ROOM_SCOPE = 'equipment' as const;

/**
 * Wire event names emitted on the `equipment` WebSocket namespace.
 * - `POWER_UNIT_STATUS_CHANGED` is a delta relayed from the internal
 *   `Events.POWER_UNIT_STATUS_CHANGED` bus (producers:
 *   `PowerUnitsService`, `InspectionService`, `DropHookService`).
 * - `POWER_UNIT_LOCATION_UPDATED` is the location-update relay (producer
 *   shipped via #458; tracking.service emits whenever an active shipment
 *   with a `powerUnitId` reports a GPS tick).
 * - `POWER_UNIT_STATUS_UPDATE` is reserved for an initial snapshot the
 *   frontend can request via `subscribe-power-unit-status`. v1 ships
 *   without computing the snapshot — frontend's HTTP fallback covers
 *   first-paint until we add the aggregator.
 * - `ERROR` is gateway-initiated only (handshake JWT verification fail).
 */
export const EQUIPMENT_WS_EVENTS = {
  POWER_UNIT_STATUS_CHANGED: 'power-unit-status-changed',
  POWER_UNIT_LOCATION_UPDATED: 'power-unit-location-updated',
  POWER_UNIT_STATUS_UPDATE: 'power-unit-status-update',
  ERROR: 'error',
} as const;

export type EquipmentWsEventName = (typeof EQUIPMENT_WS_EVENTS)[keyof typeof EQUIPMENT_WS_EVENTS];

/**
 * Wire payload for `POWER_UNIT_STATUS_CHANGED`. Status strings are the
 * raw `PowerUnitStatus` enum values (already lowercase: `'available'`,
 * `'assigned'`, `'maintenance'`, `'out_of_service'`, `'retired'`) — no
 * gateway-side translation. Frontend consumers that need aggregated
 * buckets ("active" / "inactive" / "maintenance") map these themselves.
 */
export interface PowerUnitStatusChangedWsPayload {
  readonly powerUnitId: string;
  readonly previousStatus: string;
  readonly status: string;
}

/**
 * Wire payload for `POWER_UNIT_LOCATION_UPDATED`. Nested
 * `location: { lat, lng }` shape (frontend hook contract — flat
 * `latitude`/`longitude` would force a frontend-side rename). `speed`
 * / `heading` / `accuracy` are optional GPS-meta fields the backend
 * may or may not have for a given tick.
 */
export interface PowerUnitLocationUpdatedWsPayload {
  readonly powerUnitId: string;
  readonly location: { readonly lat: number; readonly lng: number };
  readonly speed?: number;
  readonly heading?: number;
  readonly accuracy?: number;
  readonly timestamp: Date;
}
